import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { FieldText } from "../fields/Text/Text";
import { GoogleButton } from "./GoogleButton";
import {
    SignInMethodRow,
    disconnectGoogle,
    fetchSignInMethods,
    googleConnect,
    googleErrorCode,
    googleMessage,
    sendPasswordSetupEmail
} from "./googleSignIn";

// Settings › Sign-in methods (brand + RMN personal settings, expert
// settings and internal staff settings). The password row is read from the account; the Google row
// from its connection. Connect goes through Google and comes back here
// (GoogleReturnPage → location.state.googleConnect) to finish with the
// password; disconnect asks for the password too. The API attaches Google
// to the signed-in account only and enforces every rule shown here.

const SETTINGS_PATH = "/settings/sign-in-methods";

const passwordSchema = Yup.object().shape({
    password: Yup.string().required("Enter your password")
});

const sameEmail = (a?: string | null, b?: string | null) =>
    !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

const primaryButton =
    "bg-accent text-sm text-white py-2.5 px-5 rounded-xl focus:outline-none focus:shadow-outline disabled:opacity-60 disabled:cursor-not-allowed";

interface PendingConnect {
    flowId: string;
    email: string;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <Box className="rounded-xl border p-4 sm:p-5 flex flex-col gap-3" sx={{ borderColor: "var(--border, #e5e7eb)" }}>
            <Typography className="text-text-1 font-bold">{title}</Typography>
            {children}
        </Box>
    );
}

function PasswordConfirm({
    submitLabel,
    onSubmit,
    onCancel
}: {
    submitLabel: string;
    onSubmit: (password: string, helpers: FormikHelpers<{ password: string }>) => Promise<void>;
    onCancel: () => void;
}) {
    return (
        <Formik
            initialValues={{ password: "" }}
            validationSchema={passwordSchema}
            validateOnChange={false}
            onSubmit={(values, helpers) => onSubmit(values.password, helpers)}
        >
            {({ isSubmitting, status }) => (
                <Form className="flex flex-col gap-3 max-w-[420px]">
                    <FieldText label="Your password" name="password" type="password" placeholder="Password" fullWidth />
                    <Box className="flex items-center gap-3">
                        <button className={primaryButton} type="submit" disabled={isSubmitting}>
                            {submitLabel}
                        </button>
                        <Button variant="text" onClick={onCancel} disabled={isSubmitting}>
                            Cancel
                        </Button>
                    </Box>
                    {status?.error && <div className="text-left text-error text-xs font-bold">{status.error}</div>}
                </Form>
            )}
        </Formik>
    );
}

export default function SignInMethods() {
    const location = useLocation();
    const navigate = useNavigate();
    const routeState = location.state as { googleConnect?: PendingConnect; googleConnectError?: string } | null;
    const pending = routeState?.googleConnect;
    // A Connect that failed or was cancelled at Google comes back with its message.
    const connectError = routeState?.googleConnectError;
    const [methods, setMethods] = useState<SignInMethodRow[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [disconnecting, setDisconnecting] = useState(false);
    const [setupSentTo, setSetupSentTo] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const res = await fetchSignInMethods();
            setMethods(res.methods);
            setLoadError(null);
        } catch (e) {
            const code = googleErrorCode(e);
            setLoadError(
                code === "GoogleNotAvailableForAccount"
                    ? "Sign-in methods aren't available for this account."
                    : googleMessage(code)
            );
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const clearPending = () => navigate(SETTINGS_PATH, { replace: true, state: null });

    const passwordRow = methods?.find((m) => m.method === "password") as
        | Extract<SignInMethodRow, { method: "password" }>
        | undefined;
    const googleRow = methods?.find((m) => m.method === "google") as
        | Extract<SignInMethodRow, { method: "google" }>
        | undefined;
    const accountEmail = passwordRow?.email || "";
    const hasPassword = !!passwordRow?.set;

    const sendSetup = async () => {
        try {
            await sendPasswordSetupEmail(accountEmail);
            setSetupSentTo(accountEmail);
        } catch {
            toast.error("We couldn't send the email. Please try again.");
        }
    };

    const confirmConnect = async (password: string, helpers: FormikHelpers<{ password: string }>) => {
        if (!pending) return;
        helpers.setStatus(undefined);
        try {
            const res = await googleConnect(pending.flowId, password);
            setMethods(res.methods);
            toast.success("Google is connected.");
            clearPending();
        } catch (e) {
            const code = googleErrorCode(e);
            if (code === "LoginCredentialsIncorrect") {
                helpers.setErrors({ password: googleMessage(code) });
            } else {
                helpers.setStatus({ error: googleMessage(code) });
            }
        }
        helpers.setSubmitting(false);
    };

    const confirmDisconnect = async (password: string, helpers: FormikHelpers<{ password: string }>) => {
        helpers.setStatus(undefined);
        try {
            const res = await disconnectGoogle(password);
            setMethods(res.methods);
            setDisconnecting(false);
            toast.success("Google is disconnected.");
        } catch (e) {
            const code = googleErrorCode(e);
            if (code === "GoogleNotConnected") {
                // This tab was out of date (disconnected elsewhere): show the
                // real state instead of an error under a stale "Connected".
                setDisconnecting(false);
                toast(googleMessage(code));
                load();
            } else if (code === "LoginCredentialsIncorrect") {
                helpers.setErrors({ password: googleMessage(code) });
            } else {
                helpers.setStatus({ error: googleMessage(code) });
            }
        }
        helpers.setSubmitting(false);
    };

    const connectButton = (label?: string) => (
        <Box className="max-w-[320px]">
            <GoogleButton intent="connect" returnPath={SETTINGS_PATH} loginHint={accountEmail || undefined} label={label} />
        </Box>
    );

    const renderPending = () => {
        if (connectError && methods) {
            return (
                <Section title="Connect Google">
                    <Typography className="text-text-1 text-sm" data-testid="connect-error">
                        {connectError}
                    </Typography>
                    <Box className="flex flex-wrap items-center gap-3">
                        {hasPassword && connectButton("Try again with Google")}
                        <Button variant="text" onClick={clearPending}>
                            Close
                        </Button>
                    </Box>
                </Section>
            );
        }
        if (!pending || !methods) return null;
        if (!sameEmail(pending.email, accountEmail)) {
            return (
                <Section title="Connect Google">
                    <Typography className="text-text-1 text-sm">
                        That Google account uses <b>{pending.email}</b>, but your account email is <b>{accountEmail}</b>.
                        Choose a Google account that uses {accountEmail}.
                    </Typography>
                    <Box className="flex flex-wrap items-center gap-3">
                        {connectButton("Try again with Google")}
                        <Button variant="text" onClick={clearPending}>
                            Cancel
                        </Button>
                    </Box>
                </Section>
            );
        }
        if (!hasPassword) {
            return (
                <Section title="Connect Google">
                    <Typography className="text-text-1 text-sm">
                        Connecting Google asks for your password, and this account doesn&apos;t have one yet. Set a password
                        first, then connect Google.
                    </Typography>
                    <Box>
                        <Button variant="text" onClick={clearPending}>
                            Cancel
                        </Button>
                    </Box>
                </Section>
            );
        }
        return (
            <Section title="Connect Google">
                <Typography className="text-text-1 text-sm">
                    Connect Google as <b>{pending.email}</b>. Enter your password to confirm.
                </Typography>
                <PasswordConfirm submitLabel="Connect Google" onSubmit={confirmConnect} onCancel={clearPending} />
            </Section>
        );
    };

    return (
        <div className="w-full">
            <h3 className="text-text-1 text-xl sm:text-3xl mb-3 font-bold uppercase font-[CircularStd]">
                Sign-in Methods
            </h3>
            <Typography className="text-text-2 text-sm mb-6">The ways you can sign in to this account.</Typography>

            {!methods && !loadError && <CircularProgress size={24} />}
            {loadError && <div className="text-left text-error text-sm font-bold">{loadError}</div>}

            {methods && (
                <Box className="flex flex-col gap-4">
                    {renderPending()}

                    <Section title="Email & password">
                        <Typography className="text-text-1 text-sm">{accountEmail}</Typography>
                        <Typography className="text-text-2 text-sm" data-testid="password-status">
                            {hasPassword ? "Password set" : "No password"}
                        </Typography>
                        {!hasPassword &&
                            (setupSentTo ? (
                                <Typography className="text-text-2 text-sm">
                                    We emailed a link to set your password to {setupSentTo}.
                                </Typography>
                            ) : (
                                <Box>
                                    <button className={primaryButton} type="button" onClick={sendSetup}>
                                        Set a password
                                    </button>
                                </Box>
                            ))}
                    </Section>

                    <Section title="Google">
                        {googleRow?.status === "connected" && (
                            <Typography className="text-text-1 text-sm" data-testid="google-status">
                                Connected as {googleRow.email}
                            </Typography>
                        )}
                        {googleRow?.status === "needs_reconnecting" && (
                            <Typography className="text-text-1 text-sm" data-testid="google-status">
                                Needs reconnecting. This Google account uses {googleRow.email}, but your account email is{" "}
                                {accountEmail}. Connect a Google account that uses {accountEmail}; reconnecting the same
                                Google account won&apos;t fix this.
                            </Typography>
                        )}
                        {googleRow?.status === "not_connected" && (
                            <Typography className="text-text-2 text-sm" data-testid="google-status">
                                Not connected
                            </Typography>
                        )}

                        {googleRow?.status !== "connected" && !pending && !connectError && (
                            hasPassword ? (
                                connectButton()
                            ) : (
                                <Typography className="text-text-2 text-sm">
                                    Set a password first: connecting Google asks for it.
                                </Typography>
                            )
                        )}

                        {googleRow && googleRow.status !== "not_connected" && !disconnecting && (
                            hasPassword ? (
                                <Box>
                                    <Button variant="outlined" onClick={() => setDisconnecting(true)}>
                                        Disconnect
                                    </Button>
                                </Box>
                            ) : (
                                <Typography className="text-text-2 text-sm" data-testid="no-password-note">
                                    {googleRow.status === "connected"
                                        ? "Google is the only way into this account. Set a password before disconnecting it."
                                        : "Right now nothing signs in to this account: it has no password, and this Google account no longer matches its email. Set a password above, then connect a Google account that uses your current email."}
                                </Typography>
                            )
                        )}
                        {disconnecting && (
                            <>
                                <Typography className="text-text-1 text-sm">
                                    Enter your password to disconnect Google. You&apos;ll sign in with your email and
                                    password.
                                </Typography>
                                <PasswordConfirm
                                    submitLabel="Disconnect Google"
                                    onSubmit={confirmDisconnect}
                                    onCancel={() => setDisconnecting(false)}
                                />
                            </>
                        )}
                    </Section>
                </Box>
            )}
        </div>
    );
}
